import React, { useState, useRef, useEffect, useImperativeHandle } from "react";
import Card from "../../../modules/Card";
import OTPInput from "../../../modules/OTPInput";
import api_requests from "../../../resources/api_requests";
import GreenCheck from "../../../modules/icons/GreenCheck";
import RedCross from "../../../modules/icons/RedCross";
import Spinner from "../../../modules/Spinner";
import { useTranslation } from "react-i18next";

const ResendCountdown = React.forwardRef(
  (
    { handleResend, remainingAttempts, setRemainingAttempts, remainingResend },
    ref,
  ) => {
    const [countdown, setCountdown] = useState(0);
    const [lockCountdown, setLockCountdown] = useState(0);

    const countdownIntervalRef = useRef(null);
    const lockIntervalRef = useRef(null);
    const { t } = useTranslation();

    useImperativeHandle(ref, () => ({
      startCountdown,
    }));

    const startCountdown = () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      setCountdown(60);

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const startLockCountdown = () => {
      if (lockIntervalRef.current) {
        clearInterval(lockIntervalRef.current);
      }

      setLockCountdown(300);

      lockIntervalRef.current = setInterval(() => {
        setLockCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(lockIntervalRef.current);
            setRemainingAttempts(3);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    useEffect(() => {
      startCountdown();

      return () => {
        clearInterval(countdownIntervalRef.current);
        clearInterval(lockIntervalRef.current);
      };
    }, []);

    useEffect(() => {
      if (remainingAttempts <= 0) {
        startLockCountdown();
      }
    }, [remainingAttempts]);

    const formatTime = (seconds) => {
      const m = String(Math.floor(seconds / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      return `${m}:${s}`;
    };

    if (remainingResend <= 0) {
      return (
        <div className="mt-4 text-sm text-gray-600">
          {t("max_otp_attempts")}
        </div>
      );
    }

    if (remainingAttempts <= 0 && lockCountdown > 0) {
      return (
        <div className="mt-4 text-sm text-gray-600">
          Too many failed attempts. Retry after{" "}
          <span className="text-[#7953D2] underline">
            {formatTime(lockCountdown)}
          </span>{" "}
          mins
        </div>
      );
    }

    return (
      <div className="mt-4 text-sm text-gray-600 items-center gap-2">
        {remainingAttempts < 3
          ? `${remainingAttempts} attempts remaining. `
          : t("didnt_receive_code")}
        <button
          disabled={countdown > 0}
          className="text-[#7953D2] underline disabled:cursor-not-allowed disabled:text-stone-300"
          onClick={() => {
            handleResend();
            startCountdown();
          }}
        >
          Resend OTP
        </button>{" "}
        {countdown > 0 && <span>in {countdown} sec</span>}
      </div>
    );
  },
);

const OTP_VALIDATION_STATE_NEUTRAL = 0;
const OTP_VALIDATION_STATE_CORRECT = 1;
const OTP_VALIDATION_STATE_INCORRECT = 2;
const OTP_VALIDATION_STATE_VERIFYING = 3;

const OTPValidationScreen = ({ onNext, selectedCountry, ...formData }) => {
  const countdownComponentRef = useRef(null);
  const otpComponentRef = useRef(null);
  const identifier = selectedCountry === "india" ? formData.contact : formData.email;
  const identifierLabel = selectedCountry === "india" ? "contact" : "email";
  const otpValidationResponseRef = useRef(null);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [remainingResend, setRemainingResend] = useState(3);
  const { t } = useTranslation();

  const [otpValidationState, setOTPValidationState] = useState(
    OTP_VALIDATION_STATE_NEUTRAL,
  );
  const [errors, setErrors] = useState({});

  console.log("values from screen1", formData);

  const onOTPSubmit = (otp) => {
    setOTPValidationState(OTP_VALIDATION_STATE_VERIFYING);
    const apiParams = {
      otp,
      above18_fair_use: true,
      country_of_registration: selectedCountry === 'india' ? 'IN' : 'ID',
    };

    if (selectedCountry === "india") {
      apiParams.mobile = identifier;
    } else if (selectedCountry === "indonesia") {
      apiParams.email = identifier;
    }

    api_requests
      .verify_otp(apiParams)
      .then((res) => {
        console.log("otp validation", res);
        // set_token(res.access_token);
        // set_register_data((prev) => ({ ...prev, token: res.access_token }));
        // set_otp_failed(false);
        // set_is_otp_verified(true);
        // set_screen(SCREENS.register_step_2);
        console.log(res?.success);
        if (!res?.success) {
          setOTPValidationState(OTP_VALIDATION_STATE_INCORRECT);
          setRemainingAttempts((prev) => prev - 1);
        } else {
          setOTPValidationState(OTP_VALIDATION_STATE_CORRECT);
        }

        otpValidationResponseRef.current = res;
      })
      .catch((err) => {
        console.log(
          "err?.response?.data?.success",
          err?.response?.data?.success,
        );
        if (err?.response?.data?.success === false) {
          setOTPValidationState(OTP_VALIDATION_STATE_INCORRECT);
          setRemainingAttempts((prev) => prev - 1);
        } else {
          setOTPValidationState(OTP_VALIDATION_STATE_NEUTRAL);
        }
      });
  };

  function handleResend() {
    countdownComponentRef.current?.startCountdown();
    otpComponentRef.current?.reset();
    console.log("data resend iotp", formData, {
      [identifierLabel]: identifier,
    });
    setOTPValidationState(OTP_VALIDATION_STATE_NEUTRAL);

    const resendParams = {
      country_of_registration: selectedCountry === "india" ? "IN" : "ID",
    };
    if (selectedCountry === "india") {
      resendParams.mobile = identifier;
    } else if (selectedCountry === "indonesia") {
      resendParams.email = identifier;
    }

    api_requests
      .request_otp(resendParams)
      .then((res) => {
        // MARK::- HANDLE ERROR HERE
        console.log(res);
        // onNext(formData);
        setRemainingResend((prev) => prev - 1);
      });
  }

  function handleSubmit() {
    onNext(otpValidationResponseRef.current);
  }

  return (
    <div className=" px-6 mt-10 ">
      <div className="flex flex-col items-center gap-0.5 justify-center">
        <p className="font-custom  text-2xl font-semibold text-[#7953D2] ">
          {t("validate_user")}
        </p>
        <p className="font-custom text-sm text-xs font-medium text-dark_gray ">
          {t("enter_otp_sent_to")} ******-
          {identifier.slice(6)}
        </p>
      </div>

      <Card>
        <div className="mb-1 text-violet-900 text-xs font-semibold">OTP</div>
        <div className="mb-4 flex items-center gap-6">
          <OTPInput
            onSubmit={onOTPSubmit}
            ref={otpComponentRef}
            isDisabled={
              otpValidationState === OTP_VALIDATION_STATE_VERIFYING ||
              remainingAttempts <= 0
            }
            otpValidationState={otpValidationState}
            OTP_VALIDATION_STATE_INCORRECT={OTP_VALIDATION_STATE_INCORRECT}
          />
          {otpValidationState === OTP_VALIDATION_STATE_CORRECT && (
            <GreenCheck />
          )}
        </div>
        <ResendCountdown
          handleResend={handleResend}
          ref={countdownComponentRef}
          remainingAttempts={remainingAttempts}
          setRemainingAttempts={setRemainingAttempts}
          remainingResend={remainingResend}
        />

        <div className="mt-7">
          <button
            onClick={handleSubmit}
            className={`font-custom w-full py-3 rounded-[30px] text-base font-semibold
            ${
              otpValidationState !== OTP_VALIDATION_STATE_CORRECT
                ? "bg-gray text-gray-200 cursor-not-allowed"
                : "bg-[#492897] text-white"
            }`}
            disabled={otpValidationState !== OTP_VALIDATION_STATE_CORRECT}
          >
            {otpValidationState === OTP_VALIDATION_STATE_VERIFYING ? (
              <Spinner heightResponsive />
            ) : (
              <span>{t("continue")}</span>
            )}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default OTPValidationScreen;